/** @format */
import React, { useContext, useMemo } from 'react';

import {
    ListingTileItem,
    ListingTilesQuery,
    ListingTilesResponse, // eslint-disable-next-line boundaries/element-types -- Touch and change -- We shouldn't import from custom-server into features
} from '@toucan/custom-server/services/contentful/queries/listing-tiles';

import { ContentfulQueryId } from '@toucan/common/contentful';

import { MarketContext } from '@toucan/client/global/market';

import { CardBackgroundColor } from '@toucan/client/shared/components/card-background';
import { useContentful } from '@toucan/client/shared/hooks/use-contentful';

import { insertContentfulTiles } from '@toucan/client/features/search/components/listing-page/helpers/listing-tiles';

import { ListingTile } from './components/listing-tile';
import { ListingTileSkeleton } from './components/listing-tile/skeleton';

/**
 * We've updated our theme to use the new color palette, so we need to transform the old color names to the new ones.
 * As these are done in contentful, we're adding an adapter here so we don't have to update contentful as part of this migration.
 */
const transformColor = (color: string) => {
    switch (color) {
        case 'sky':
            return CardBackgroundColor.PINK;
        default:
            return color as CardBackgroundColor;
    }
};

const transformListingTilesProps = (data?: ListingTilesResponse) =>
    data?.siteContentListingTileCollection?.items.map(
        (item: ListingTileItem) => ({
            id: item.sys.id,
            heading: item.heading,
            gridImage: item.gridImage.url ?? item.gridImage.urlFallback ?? '',
            gridImageAlt: item.gridImage.description,
            color: transformColor(item.variant.toLowerCase()),
        })
    ) ?? [];

const skeletonTiles = [1, 2, 3].map((id) => <ListingTileSkeleton key={id} />);

export const useContentfulTiles = (isPageLoading: boolean) => {
    const { activeMarket } = useContext(MarketContext);

    const queryVariables = { marketCode: activeMarket.code };
    const queryOptions = { enabled: Boolean(activeMarket.code) };

    const { data, isLoading } = useContentful<
        ListingTilesResponse,
        ListingTilesQuery
    >(ContentfulQueryId.LISTING_TILES, queryVariables, queryOptions);

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const showLoadingState = isLoading || isPageLoading;

    const items = useMemo(() => {
        return showLoadingState
            ? skeletonTiles
            : transformListingTilesProps(data).map(
                  ({ id, color, heading, gridImage, gridImageAlt }) => (
                      <ListingTile
                          key={id}
                          color={color}
                          heading={heading}
                          gridImage={gridImage}
                          gridImageAlt={gridImageAlt}
                      />
                  )
              );
    }, [showLoadingState, data]);

    return {
        items,
        isLoading,
        insertContentfulTiles,
    };
};
